import * as React from 'react';
import './order-with-keyword.scss';
import { Page } from 'components/page/page';
import { Card } from 'components/card/card';
import { Table } from 'components/table/table';
import { Order, PaginationList } from 'core/domain';
import { Badge } from 'components';
import { CopyOutlined, DollarCircleOutlined, EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
    confirmAndPayoutOrder,
    getAllOrderStateTypes,
    getAllOrderTypes,
    searchOrderWithKeyword,
} from 'core/repositories/orders-transactions';
import { Button, buttonType } from 'components/button/button';
import { Input } from 'components/input/input';
import { Enum } from '../../../../core/domain/commons/common';
import moment from 'jalali-moment';
import { digitSperator, faNumToEnNum } from 'core/utils/decimalExtentions';
import { copyToClipboard } from 'core/utils/copyToClipboard';
import { getIconPath } from 'core/utils/getIconPath';
import { OrderEditModal } from './edit-modal/edit-modal';
import { OrderDetailModal } from './order-detail/order-detail';
import { Modal } from 'components/modal/modal';
import { Dropdown } from '../../../../components/dropdown/dropdown';
import DatePicker from 'react-multi-date-picker';
import persian from 'react-date-object/calendars/persian';
import persian_fa from 'react-date-object/locales/persian_fa';
import { ReactComponent as CalendarIcon } from 'assets/icons/dash-line-icons/calendar.svg';
import { getOrderTransactionStateBadgeType } from '../orders-transactions/script';
import { jFormat } from 'core/utils/jFormat';

interface OrderTransaction {
    id: string;
    orderTransactionTrackingCode: string;
    phoneNumber: string;
    fullName: string;
    orderId: string;
    userId: string;
    sourceSymbol: string;
    destinationSymbol: string;
    sourceAmount: number;
    destinationAmount: number;
    createdOn: string;
    orderTrackingCode: string;
    depositTransactionTrackingCode: string;
    withdrawalTransactionTrackingCode: string;
    state: Enum;
    type: Enum;
}

interface FilterValues {
    search: string;
    state: Enum;
    type?: Enum;
    startDate: string;
    endDate: string;
    marketType?: Enum;
}

const empty_filters = {
    search: '',
    state: {
        id: -2,
        title: 'همه وضعیت ها',
    },
    type: {
        id: -1,
        title: 'همه نوع ها',
    },
    marketType: {
        id: 0,
        title: 'همه ارز ها',
    },
    startDate: moment().locale('fa').subtract(10, 'days').format('jYYYY/jMM/jDD'),
    endDate: '',
};

export const SearchOrderWithKeyword: React.FC = () => {
    const [detailModalIsOpen, setDetailModalIsOpen] = React.useState<boolean>(false);

    const [selectedItem, setSelectedItem] = React.useState<OrderTransaction>();
    const [confirmButtonLoading, setConfirmButtonLoading] = React.useState<boolean>(false);
    const [orderStateTypes, setOrderStateTypes] = React.useState<Enum[]>([]);
    const [query, setQuery] = React.useState<string>('');
    const [editModalIsOpen, setEditModalIsOpen] = React.useState<boolean>(false);
    const [orders, setOrders] = React.useState<PaginationList<OrderTransaction>>();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [buttonLoading, setButtonLoading] = React.useState<boolean>(false);
    const [deleteButtonLoading, setDeleteButtonLoading] = React.useState<boolean>(false);
    const [modalIsOpen, setModalIsOpen] = React.useState<boolean>(false);
    const [payoutModalIsOpen, setPayoutModalIsOpen] = React.useState<boolean>(false);
    const [filterValues, setFilterValues] = React.useState<FilterValues>(empty_filters);
    const [pageSize, setPageSize] = React.useState<number>(10);
    const [orderTypes, setOrderTypes] = React.useState<Enum[]>([]);
    const [marketTypes, setMarketTypes] = React.useState<Enum[]>([]);

    const startDateRef = React.useRef();
    const endDateRef = React.useRef();

    const navigate = useNavigate();

    const filterHandle = (clearFilter?: boolean) => {
        var _query = ``;

        if (filterValues.type.id !== -1) _query += `${_query.includes('?') ? '&' : '?'}type=${filterValues.type?.id}`;
        if (filterValues.state?.id !== -2)
            _query += `${_query.includes('?') ? '&' : '?'}state=${filterValues.state?.id}`;
        if (filterValues.marketType?.id !== 0)
            _query += `${_query.includes('?') ? '&' : '?'}marketType=${filterValues.marketType?.id}`;
        if (filterValues.startDate !== '')
            _query += `${_query.includes('?') ? '&' : '?'}startDate=${jFormat(filterValues.startDate)}`;
        if (filterValues.endDate !== '')
            _query += `${_query.includes('?') ? '&' : '?'}endDate=${jFormat(filterValues.endDate)}`;
        if (filterValues.search !== '') _query += `${_query.includes('?') ? '&' : '?'}keyword=${filterValues.search}`;

        setQuery(_query);
        if (filterValues.search !== '') {
            searchOrderWithKeyword(
                clearFilter ? setDeleteButtonLoading : _query !== '' ? setButtonLoading : setLoading,
                setOrders,
                1,
                pageSize,
                clearFilter ? '' : _query,
                filterValues.search,
            );
        }
    };

    async function fetches() {
        await getAllOrderTypes(setLoading, setOrderTypes);
        getAllOrderStateTypes(setLoading, setOrderStateTypes);
    }

    React.useEffect(() => {
        fetches();
    }, []);

    const onPayoutIconClickHandle = (item) => {
        setSelectedItem(item);
        setPayoutModalIsOpen(true);
    };

    const columns: any[] = React.useMemo(
        () => [
            {
                name: 'شناسه',
                id: 'orderTransactionTrackingCode',
                selector: (row: OrderTransaction) => row.orderTransactionTrackingCode,
                width: '14%',
                cell: (row: OrderTransaction) => (
                    <span className="en copiable">
                        {row.orderTransactionTrackingCode}
                        <CopyOutlined onClick={() => copyToClipboard(row.orderTransactionTrackingCode, 'شناسه سفارش')} />
                    </span>
                ),
            },
            {
                name: 'نام',
                id: 'fullName',
                selector: (row: OrderTransaction) => row.fullName,
                width: '9%',
            },
            {
                name: 'موبایل',
                id: 'phoneNumber',
                selector: (row: OrderTransaction) => row.phoneNumber,
                width: '8%',
            },
            {
                name: 'جفت ارز',
                id: 'pair',
                selector: (row: OrderTransaction) => row?.sourceSymbol + row.destinationSymbol,
                cell: (row: OrderTransaction) => (
                    <div className="cell pair">
                        <img src={getIconPath(row.sourceSymbol.toLowerCase())} />
                        <span className="en">{row.sourceSymbol}</span>
                        {` `} / {` `}
                        <span className="en">{row.destinationSymbol}</span>
                        <img src={getIconPath(row.destinationSymbol.toLowerCase())} />
                    </div>
                ),
                width: '12%',
            },
            {
                name: 'مبلغ مبدا',
                id: 'sourceAmount',
                selector: (row: OrderTransaction) =>
                    row.sourceSymbol.toLowerCase() === 'irt'
                        ? digitSperator(row.sourceAmount.toFixed(0))
                        : row.sourceAmount,
                width: '7%',
            },
            {
                name: 'مبلغ مقصد',
                id: 'destinationAmount',
                selector: (row: OrderTransaction) =>
                    row.destinationSymbol.toLowerCase() === 'irt'
                        ? digitSperator(row.destinationAmount.toFixed(0))
                        : row.destinationAmount,
                width: '8%',
            },
            {
                name: 'تاریخ ثبت سفارش',
                id: 'dateTime',
                selector: (row: OrderTransaction) => row?.createdOn || '',
                sortable: true,
                width: '10%',
                cell: (row: OrderTransaction) => (
                    <span>
                        {moment.utc(row.createdOn).local().format('HH:mm')}{' '}
                        {moment.utc(row?.createdOn).locale('fa').local().format('YYYY/MM/DD')}{' '}
                    </span>
                ),
            },
            {
                name: 'وضعیت',
                id: 'state',
                width: '14%',
                selector: (row: OrderTransaction) => row.state,
                cell: (row: OrderTransaction) => (
                    <Badge text={row.state.title} type={getOrderTransactionStateBadgeType(row?.state?.id)} />
                ),
            },

            {
                name: 'نوع سفارش',
                id: 'type',
                width: '8%',
                selector: (row: OrderTransaction) => row.type,
                cell: (row: OrderTransaction) => (
                    <Badge
                        text={row.type.title}
                        type={row.type.title == 'موفق' ? 'success' : row.type.title == 'ناموفق' ? 'error' : 'info'}
                    />
                ),
            },

            {
                name: 'عملیات',
                id: 'action',
                width: '10%',
                cell: (row: OrderTransaction) => (
                    <div className="actions">
                        <EditOutlined
                            onClick={() => {
                                setSelectedItem(row);
                                setEditModalIsOpen(true);
                            }}
                        />

                        <DollarCircleOutlined onClick={() => onPayoutIconClickHandle(row)} />

                        <InfoCircleOutlined
                            onClick={() => {
                                setSelectedItem(row);
                                setDetailModalIsOpen(true);
                            }}
                        />
                    </div>
                ),
            },
        ],
        [onPayoutIconClickHandle]
    );

    // const columns: any[] = [
    //     {
    //         name: 'شناسه',
    //         id: 'orderTransactionTrackingCode',
    //         selector: (row: OrderTransaction) => row.orderTransactionTrackingCode,
    //         width: '14%',
    //         cell: (row: OrderTransaction) => (
    //             <span className="en copiable">
    //                 {row.orderTransactionTrackingCode}
    //                 <CopyOutlined onClick={() => copyToClipboard(row.orderTransactionTrackingCode, 'شناسه سفارش')} />
    //             </span>
    //         ),
    //     },
    //     {
    //         name: 'جفت ارز',
    //         id: 'pair',
    //         selector: (row: OrderTransaction) => row?.sourceSymbol + row.destinationSymbol,
    //         cell: (row: OrderTransaction) => (
    //             <div className="cell pair">
    //                 <img src={getIconPath(row.sourceSymbol?.toLowerCase() ?? '')} />
    //                 <span className="en">{row.sourceSymbol}</span>
    //                 {` `} / {` `}
    //                 <span className="en">{row.destinationSymbol}</span>
    //                 <img src={getIconPath(row.destinationSymbol?.toLowerCase() ?? '')} />
    //             </div>
    //         ),
    //         width: '14%',
    //     },
    //     {
    //         name: 'مبلغ مبدا',
    //         id: 'sourceAmount',
    //         selector: (row: OrderTransaction) =>
    //             row.sourceSymbol?.toLowerCase() === 'irt'
    //                 ? digitSperator(row.sourceAmount.toFixed(0))
    //                 : row.sourceAmount,
    //         width: '15%',
    //     },
    //     {
    //         name: 'مبلغ مقصد',
    //         id: 'destinationAmount',
    //         selector: (row: OrderTransaction) =>
    //             row.destinationSymbol?.toLowerCase() === 'irt'
    //                 ? digitSperator(row.destinationAmount.toFixed(0))
    //                 : row.destinationAmount,
    //         width: '15%',
    //     },
    //     {
    //         name: 'تاریخ ثبت سفارش',
    //         id: 'dateTime',
    //         selector: (row: OrderTransaction) => row?.createdOn || '',
    //         sortable: true,
    //         width: '14%',
    //         cell: (row: OrderTransaction) => (
    //             <span>
    //                 {moment.utc(row?.createdOn).locale('fa').local().format('HH:mm')}{' '}
    //                 {moment.utc(row?.createdOn).locale('fa').local().format('YYYY/MM/DD')}{' '}
    //             </span>
    //         ),
    //     },
    //     {
    //         name: 'وضعیت',
    //         id: 'state',
    //         width: '14%',
    //         selector: (row: OrderTransaction) => row.state,
    //         cell: (row: OrderTransaction) => (
    //             <Badge text={row.state?.title} type={getOrderTransactionStateBadgeType(row?.state?.id)} />
    //         ),
    //     },

    //     {
    //         name: 'عملیات',
    //         id: 'action',
    //         width: '14%',
    //         cell: (row: OrderTransaction) => (
    //             <div className="actions">
    //                 <EditOutlined
    //                     onClick={() => {
    //                         setSelectedItem(row);
    //                         setEditModalIsOpen(true);
    //                     }}
    //                 />

    //                 <DollarCircleOutlined onClick={() => onPayoutIconClickHandle(row)} />

    //                 <InfoCircleOutlined
    //                     onClick={() => {
    //                         setSelectedItem(row);
    //                         setDetailModalIsOpen(true);
    //                     }}
    //                 />
    //             </div>
    //         ),
    //     },
    // ];

    const clearFiltersHandle = () => {
        setFilterValues({ ...empty_filters, startDate: '' });
        filterHandle(true);
    };

    return (
        <Page className="pmx-orders-by-keyword" title="جستجوی سفارش با کلیدواژه">
            {selectedItem && (
                <OrderEditModal
                    orderStates={orderStateTypes}
                    isOpen={editModalIsOpen}
                    setIsOpen={setEditModalIsOpen}
                    item={{
                        ...selectedItem,
                        trackingCode: selectedItem.orderTransactionTrackingCode,
                        orderTransactionState: selectedItem.state,
                        orderTransactionType: selectedItem.type,
                    }}
                    onClose={() => filterHandle()}
                />
            )}
            <Modal setIsOpen={setPayoutModalIsOpen} isOpen={payoutModalIsOpen} className="confirm-payout-modal">
                <p>آیا از تایید و تسویه تراکنش کاربر {selectedItem?.orderTransactionTrackingCode} اطمینان دارید؟</p>

                <div className="buttons">
                    <Button
                        type={buttonType.info}
                        text="بله"
                        onClick={() =>
                            confirmAndPayoutOrder(
                                setConfirmButtonLoading,
                                { trackingCode: selectedItem?.orderTransactionTrackingCode },
                                () => setPayoutModalIsOpen(false),
                                () => filterHandle(),
                            )
                        }
                        loading={confirmButtonLoading}
                    />
                    <Button type={buttonType.error} text="خیر" onClick={() => setPayoutModalIsOpen(false)} />
                </div>
            </Modal>
            <Card title={orders?.items && `نمایش سفارشات با کلیدواژه "${filterValues.search}"`} blur={loading}>
                {selectedItem && (
                    <OrderDetailModal
                        selectedRow={{
                            ...selectedItem,
                            trackingCode: selectedItem.orderTransactionTrackingCode,
                            orderTransactionState: selectedItem.state,
                            orderTransactionType: selectedItem.type,
                        }}
                        isOpen={detailModalIsOpen}
                        setIsOpen={setDetailModalIsOpen}
                        trackingId={selectedItem?.orderTransactionTrackingCode}
                    />
                )}

                <div className="filter-row">
                    <div className="fields">
                        <Input
                            value={filterValues.search}
                            className="search"
                            placeholder="کلیدواژه را وارد کنید ..."
                            onPressEnter={filterHandle}
                            name="search"
                            onChange={(e) => setFilterValues({ ...filterValues, search: e.target.value })}
                        />
                        {orderTypes.length > 0 && (
                            <Dropdown
                                items={orderTypes}
                                onChangeHandle={(value) =>
                                    setFilterValues({
                                        ...filterValues,
                                        type: orderTypes.find((l) => l.id === value),
                                    })
                                }
                                selectedOption={filterValues.type}
                            />
                        )}
                        {orderStateTypes.length > 0 && (
                            <Dropdown
                                items={orderStateTypes}
                                onChangeHandle={(value) =>
                                    setFilterValues({
                                        ...filterValues,
                                        state: orderStateTypes.find((l) => l.id === value),
                                    })
                                }
                                selectedOption={filterValues.state}
                            />
                        )}
                        {marketTypes.length > 0 && (
                            <Dropdown
                                items={marketTypes}
                                onChangeHandle={(value) =>
                                    setFilterValues({
                                        ...filterValues,
                                        marketType: marketTypes.find((l) => l.id === value),
                                    })
                                }
                                selectedOption={filterValues.marketType}
                            />
                        )}

                        <DatePicker
                            className="date-picker"
                            value={filterValues.startDate}
                            calendar={persian}
                            ref={startDateRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.startDate}
                                        icon={<CalendarIcon />}
                                        placeholder="از تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({ ...filterValues, startDate: e.format() });
                            }}
                        />
                        <DatePicker
                            className="date-picker"
                            value={filterValues.endDate}
                            calendar={persian}
                            ref={endDateRef}
                            locale={persian_fa}
                            render={
                                <div>
                                    <Input
                                        value={filterValues.endDate}
                                        icon={<CalendarIcon />}
                                        placeholder="تا تاریخ"
                                    />
                                </div>
                            }
                            onChange={(e) => {
                                setFilterValues({ ...filterValues, endDate: e.format() });
                            }}
                        />
                    </div>
                    <div className="buttons">
                        <Button
                            loading={buttonLoading}
                            disabled={buttonLoading}
                            text="جستجو"
                            type={buttonType.success}
                            onClick={() => filterHandle()}
                        />
                        <Button
                            loading={deleteButtonLoading}
                            disabled={deleteButtonLoading}
                            text="حذف فیلتر"
                            type={buttonType.error}
                            onClick={clearFiltersHandle}
                        />
                    </div>
                </div>
                {
                    <>
                        {orders && (
                            <Table
                                setPageSize={setPageSize}
                                blur={loading}
                                setloader={setLoading}
                                getdata={searchOrderWithKeyword}
                                setdata={setOrders}
                                pagination={orders?.pagination}
                                className="orders-table"
                                data={orders?.items}
                                column={columns}
                                filters={query}
                            />
                        )}
                    </>
                }
            </Card>
        </Page>
    );
};
